/**
 * Attachments and external links.
 *
 * One endpoint set covers both: `kind` says whether a row is an uploaded file
 * or a link to something elsewhere (an Evernote note, a Google Doc). Clients
 * render them in the same list.
 */
import { api, apiUpload, type UploadProgress } from './client'

export type ParentType = 'story' | 'subtask' | 'comment' | 'cal_event'
export type AttachmentKind = 'file' | 'link'
export type LinkProvider = 'evernote' | 'google' | 'generic'

export type Attachment = {
  id: number
  kind: AttachmentKind
  parent_type: ParentType
  parent_id: number
  project_id: number | null
  user_id: number
  user_name: string | null
  user_avatar_color: string | null
  created_at: string

  // kind === 'file'
  file_name: string | null
  mime_type: string | null
  byte_size: number | null
  width: number | null
  height: number | null
  /** Pre-signed, short-lived; usable directly as an <img src>. */
  content_url: string | null
  thumb_url: string | null

  // kind === 'link'
  url: string | null
  title: string | null
  provider: LinkProvider | null
}

export const attachmentsKey = (parentType: ParentType, parentId: number | null) =>
  ['attachments', parentType, parentId] as const

export async function listAttachments(parentType: ParentType, parentId: number): Promise<Attachment[]> {
  return api<Attachment[]>('/attachments', { query: { parent_type: parentType, parent_id: parentId } })
}

export function uploadAttachment(
  parentType: ParentType,
  parentId: number,
  file: File,
  opts: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<Attachment> {
  const form = new FormData()
  form.append('parent_type', parentType)
  form.append('parent_id', String(parentId))
  form.append('file', file)
  return apiUpload<Attachment>('/attachments', form, opts)
}

export async function createLink(
  parentType: ParentType,
  parentId: number,
  url: string,
  title: string,
): Promise<Attachment> {
  return api<Attachment>('/links', {
    method: 'POST',
    body: { parent_type: parentType, parent_id: parentId, url, title },
  })
}

export async function deleteAttachment(id: number): Promise<void> {
  return api<void>(`/attachments/${id}`, { method: 'DELETE' })
}

export function isImage(a: Attachment): boolean {
  return a.kind === 'file' && (a.mime_type ?? '').startsWith('image/')
}

/** Signed URLs are handed back ready to use; this just prefers the smaller one. */
export function previewUrl(a: Attachment): string | null {
  return a.thumb_url ?? a.content_url
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Cosmetic only — the server computes and stores the authoritative provider.
 * This just avoids a round-trip before the row comes back.
 */
export function detectProvider(url: string): LinkProvider {
  try {
    const host = new URL(url).hostname.toLowerCase()
    if (/(^|\.)evernote\.com$/.test(host) || /(^|\.)yinxiang\.com$/.test(host)) return 'evernote'
    if (/(^|\.)google\.com$/.test(host)) return 'google'
    return 'generic'
  } catch {
    return 'generic'
  }
}
